import React, { useState } from "react";
import {
  faExclamationCircle,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Table,
  Button,
  CardFooter,
} from "reactstrap";
import ButtonIconTooltip from "../../common/ButtonIconTooltip";
import FalconCardHeader from "../../common/FalconCardHeader";
import PageHeader from "../../common/PageHeader";
import useSWRFetchPagination from "../../../helpers/hooks/useSWRFetchPagination";
import { fetchCondominium, fetchCondominiums } from "../../../helpers/api/condominium";
import { useSelector } from "react-redux";
import { Message, Or, PreloadState } from "../../UI/preloading/PreloadState";
import { isIterableArray } from "../../../helpers/utils";
import { Link } from "react-router-dom";
import FullPagination from "../../common/pagination/FullPagination";
import classNames from "classnames";
import Flex from "../../common/Flex";
import CondominiumRow from "./components/condominiumRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CondominiumStats from "./CondominiumStats";

const Condominiums = () => {
  const { token } = useSelector((state) => state.auth.user);
  const [textSearch, setTextSearch] = useState("");
  const [params, setParams] = useState({
    search: null,
    token,
  });

  const {
    data,
    error,
    isLoading,
    mutate,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchCondominium, params);

  return (
    <>
      <PageHeader
        title="Condominios"
        titleTag="h2"
        col={{ lg: 9 }}
        className="mb-3"
        description="Gestión de condominios."
      />
      <CondominiumStats />
      <Card>
        <FalconCardHeader title="Condominios">
          <Row>
            <Col>
              <Row className="mb-2">
                <Col className="pr-0">
                  <Flex align="center">
                    <Label className="mb-0 mr-2">Filtrar</Label>
                    <Input
                      type="select"
                      onChange={({ target }) => {
                        if (target.value === "true") {
                          setParams((prev) => ({ ...prev, banned: true }));
                        } else if (target.value === "false") {
                          setParams((prev) => ({ ...prev, banned: false }));
                        } else {
                            setParams((prev) => ({ ...prev, banned: null }));
                        }
                      }}
                    >
                      <option value="all">Todos</option>
                      <option value={false}>Activos</option>
                      <option value={true}>Desactivados</option>
                    </Input>
                  </Flex>
                </Col>
                <Col xs="auto">
                  <ButtonIconTooltip
                    icon={faPlus}
                    size="md"
                    placement="left"
                    color="falcon-default"
                    id="add"
                    text="Añadir condominio"
                    tag={Link}
                    to="/root/condominiums/create"
                  >
                    Añadir condominio
                  </ButtonIconTooltip>
                </Col>
              </Row>
              <Row>
                <Col className="d-flex">
                  <Input
                    type="text"
                    value={textSearch}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      const value = e.currentTarget.value.trim();
                      setTextSearch(value);
                      setParams((rest) => ({ ...rest, search: value || null }));
                    }}
                    onChange={(event) => {
                      const value = event.target.value;
                      setTextSearch(value);
                      if (value.trim() === "") {
                        setParams((rest) => ({ ...rest, search: null }));
                      }
                    }}
                    placeholder="Nombre, sigla, identificación, dirección o administrador..."
                    className="d-inline-block search-input mr-2"
                  />
                  <Button
                    className="d-inline-block text-nowrap"
                    color="falcon-default"
                    onClick={() => {
                      const value = textSearch.trim();
                      setTextSearch(value);
                      setParams((rest) => ({ ...rest, search: value || null }));
                    }}
                  >
                    <FontAwesomeIcon icon={faSearch} className="mr-2" />
                    Buscar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </FalconCardHeader>
        <CardBody
          className={classNames("fs--1", { "p-0": isIterableArray(data) })}
        >
          <PreloadState cond={isIterableArray(data)} loading={isLoading}>
            <Or value={true}>
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th className="align-middle">Condominio</th>
                    <th className="align-middle">Dirección</th>
                    <th className="align-middle">Estado</th>
                    <th className="align-middle">Ciudad</th>
                    <th className="align-middle">Administrador</th>
                    <th className="align-middle text-center">Estado</th>
                    <th className="align-middle text-center">Opciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((condominium, i) => (
                    <CondominiumRow key={i} {...condominium} mutate={mutate} />
                  ))}
                </tbody>
              </Table>
            </Or>
            <Or cond={!!error} value={false}>
              <Or value={false}>
                <Message
                  className="py-3"
                  color="info"
                  icon={faExclamationCircle}
                >
                  No hay condominios para mostrar.
                </Message>
              </Or>
              <Or value={true}>
                <Message
                  className="py-3"
                  color="danger"
                  icon={faExclamationCircle}
                >
                  Ha ocurrido un error. <Link to="/root/">Volver</Link>
                </Message>
              </Or>
            </Or>
          </PreloadState>
        </CardBody>
        {isIterableArray(data) && (
          <CardFooter className="border-top">
            <FullPagination
              nextPage={nextPage}
              prevPage={previousPage}
              goToPage={goToPage}
              meta={meta}
              className={"shadow-none"}
              posText={"condominios"}
              perPage={perPage}
            />
          </CardFooter>
        )}
      </Card>
    </>
  );
};

export default Condominiums;
